import React, { useState } from 'react';
import { 
  BuildingOffice2Icon, 
  CreditCardIcon, 
  ShieldCheckIcon, 
  PencilSquareIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon,
  ArrowUpCircleIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';

const CompanyProfile = () => {
  // 1. STATE MANAGEMENT
  const [companyInfo, setCompanyInfo] = useState({
    name: 'TechFlow Solutions Pvt Ltd',
    legalName: 'TechFlow Solutions Private Limited',
    industry: 'Information Technology',
    status: 'Verified',
    gst: '27AAAAA0000A1Z5',
    pan: 'ABCDE1234F',
    email: 'admin@techflow.io',
    address: '102, Innovation Park, Hitech City, Hyderabad, 500081',
  });

  const [subscription] = useState({
    plan: 'Enterprise Pro',
    employeeCount: '1,248 / 2,000',
    renewalDate: 'Oct 12, 2026',
    status: 'Active'
  });

  const [compliance] = useState({
    statutory: 94,
    policy: 100,
    auditReady: 'Yes'
  });

  // 2. HANDLERS
  const handleEdit = (section) => console.log(`Opening Edit Modal for: ${section}`);
  const handleUpgrade = () => console.log("Redirecting to Stripe/Billing portal...");
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    console.log("Copied to clipboard:", text);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-roboto text-slate-800">
      
      {/* 1. HEADER SECTION */}
      <header className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 rounded-lg flex items-center justify-center text-white shadow-inner">
            <BuildingOffice2Icon className="w-8 h-8" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-poppins text-xl font-bold text-slate-900">{companyInfo.name}</h1>
              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter">
                {companyInfo.status}
              </span>
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <GlobeAltIcon className="w-3.5 h-3.5" /> {companyInfo.industry}
            </p>
          </div>
        </div>
        <button 
          onClick={() => handleEdit('General Info')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-slate-300 rounded hover:bg-slate-50 transition-colors"
        >
          <PencilSquareIcon className="w-4 h-4" /> Edit Profile
        </button>
      </header>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* 2. COMPANY DETAILS (Main Content) */}
        <section className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
              <h2 className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">Legal & Identity</h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4">
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Legal Name</label>
                <p className="text-sm font-bold text-slate-800">{companyInfo.legalName}</p>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Registered Email</label>
                <p className="text-sm font-bold text-slate-800">{companyInfo.email}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-100 relative group">
                <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">GST Number</label>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-mono font-bold text-indigo-600">{companyInfo.gst}</p>
                  <button onClick={() => copyToClipboard(companyInfo.gst)}>
                    <DocumentDuplicateIcon className="w-4 h-4 text-slate-300 hover:text-indigo-500 cursor-pointer transition-colors" />
                  </button>
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-100">
                <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">PAN Number</label>
                <p className="text-sm font-mono font-bold text-slate-800">{companyInfo.pan}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Registered Address</label>
                <p className="text-sm text-slate-600 leading-relaxed">{companyInfo.address}</p>
              </div>
            </div>
          </div>

          {/* 4. COMPLIANCE STATUS */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">Compliance Status</h2>
            </div>
            <div className="p-5 grid grid-cols-3 gap-4">
              <div className="text-center p-3 border border-slate-100 rounded">
                <div className="text-xl font-bold text-emerald-600 mb-1">{compliance.statutory}%</div>
                <p className="text-[10px] font-bold text-slate-400 uppercase">Statutory</p>
              </div>
              <div className="text-center p-3 border border-slate-100 rounded">
                <div className="text-xl font-bold text-indigo-600 mb-1">{compliance.policy}%</div>
                <p className="text-[10px] font-bold text-slate-400 uppercase">Policy Sync</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded">
                <div className="text-xs font-bold text-emerald-700 mb-1 flex items-center justify-center gap-1">
                  <CheckBadgeIcon className="w-4 h-4" /> {compliance.auditReady}
                </div>
                <p className="text-[10px] font-bold text-emerald-600/60 uppercase">Audit Ready</p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. SUBSCRIPTION & BILLING (Sidebar Style) */}
        <aside className="space-y-6">
          <div className="bg-slate-900 rounded-lg shadow-xl overflow-hidden">
            <div className="p-5 text-white">
              <div className="flex items-center justify-between mb-4">
                <CreditCardIcon className="w-6 h-6 text-indigo-400" />
                <span className="text-[10px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/30 font-bold uppercase">
                  {subscription.status}
                </span>
              </div>
              <h3 className="text-[10px] font-bold text-indigo-300 uppercase tracking-widest mb-1">Current Plan</h3>
              <p className="text-lg font-poppins font-bold mb-6">{subscription.plan}</p>
              
              <div className="space-y-4 mb-6">
                <div>
                  <div className="flex justify-between text-[11px] font-bold mb-1.5">
                    <span className="text-slate-400 uppercase">Employee Capacity</span>
                    <span>{subscription.employeeCount}</span>
                  </div>
                  <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-indigo-500 h-full w-[62%] shadow-[0_0_8px_rgba(99,102,241,0.6)]" />
                  </div>
                </div>
                <div className="flex justify-between text-[11px] font-bold">
                  <span className="text-slate-400 uppercase">Next Renewal</span>
                  <span className="text-white">{subscription.renewalDate}</span>
                </div>
              </div>

              <button 
                onClick={handleUpgrade}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <ArrowUpCircleIcon className="w-4 h-4" /> Upgrade Plan
              </button>
            </div>
            <div className="bg-slate-800 p-3 text-center border-t border-slate-700">
              <button className="text-[10px] font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-widest">
                Download Last Invoice
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600" />
              <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">Security Settings</h3>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed mb-4 italic">
              "Your organization is currently following GDPR and ISO 27001 data residency protocols."
            </p>
            <button className="w-full py-1.5 border border-slate-200 text-[10px] font-bold text-slate-600 rounded hover:bg-slate-50 uppercase">
              Manage Data Policy
            </button>
          </div>
        </aside>

      </div>
    </div>
  );
};

export default CompanyProfile;